import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import BookingModal from "../components/BookingModal";
import ChangePasswordModal from "../components/ChangePasswordModal";
import { fmtTime12 } from "../components/DashboardUI";

const StatusBadge = ({ s }) => {
  const map = { approved: "bg-green-100 text-green-800", declined: "bg-red-100 text-red-800",
                cancelled: "bg-gray-200 text-gray-700", pending: "bg-amber-100 text-amber-800" };
  return <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-widest ${map[s] || map.pending}`}>{s}</span>;
};

export default function CustomerAccount() {
  const { user, logout, loading, refreshMe } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [recs, setRecs] = useState([]);
  const [services, setServices] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [recsLoading, setRecsLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [b, s, p, rem] = await Promise.all([
        api.get("/customer/bookings"),
        api.get("/services"),
        api.get("/promotions"),
        api.get("/customer/reminders"),
      ]);
      setBookings(b.data); setServices(s.data); setPromotions(p.data); setReminders(rem.data || []);
    } catch {}
    api.get("/customer/recommendations").then(r => setRecs(r.data.recommendations || []))
      .catch(() => setRecs([])).finally(() => setRecsLoading(false));
  };

  useEffect(() => {
    if (loading) return;
    if (!user) return nav("/login");
    if (user.role !== "customer") return nav("/");
    load();
  }, [user, loading]);

  const togglePrivacy = async () => {
    try {
      await api.patch("/customer/profile", { share_profile: !user.share_profile });
      const u = await refreshMe();
      toast.success(`Profile sharing ${u?.share_profile ? "enabled" : "disabled"}`);
    } catch { toast.error("Failed to update privacy"); }
  };

  const cancelBooking = async () => {
    if (!cancelTarget) return;
    try {
      await api.post(`/bookings/${cancelTarget.id}/cancel`, { reason: cancelTarget.reason || "" });
      toast.success("Booking cancelled");
      setCancelTarget(null);
      load();
    } catch { toast.error("Failed to cancel"); }
  };

  const requestHistory = async () => {
    try {
      await api.post("/customer/history-request");
      toast.success("History request sent to the site owner. You can also download it now.");
    } catch { toast.error("Failed"); }
  };

  const downloadHistory = async () => {
    try {
      const token = localStorage.getItem("ayame_token");
      const res = await fetch(`${BACKEND_URL}/api/customer/history-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("PDF error");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `ayame-history-${user.name}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("History downloaded");
    } catch { toast.error("Could not download PDF"); }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-[var(--muted)]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="customer-account">
      <header className="bg-white border-b border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <a href="/" className="font-display text-3xl">Ayame</a>
            <p className="text-xs uppercase tracking-widest text-[var(--muted)]">Hello, {user.name}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => setPwdOpen(true)} className="text-xs uppercase tracking-widest hover:text-[var(--accent)]" data-testid="change-pwd">Change Password</button>
            <a href="/" className="text-sm uppercase tracking-widest hover:text-[var(--accent)]">Site</a>
            <button onClick={async () => { await logout(); nav("/"); }} className="btn-outline rounded-full px-5 py-2 text-xs tracking-widest uppercase" data-testid="customer-logout">Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
        {/* Promotions banner */}
        {promotions.length > 0 && (
          <section className="mb-10" data-testid="promotions-section">
            <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
              <div>
                <p className="uppercase text-xs tracking-[0.3em] text-[var(--accent)] mb-1">Just For You</p>
                <h2 className="font-display text-3xl">Active Promotions</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {promotions.map(p => (
                <article key={p.id} className="bg-[var(--primary)] text-white p-6 flex items-center justify-between gap-4 overflow-hidden relative" data-testid={`promo-${p.id}`}>
                  <div className="relative z-10 flex-1">
                    <p className="text-[var(--accent)] text-xs uppercase tracking-widest mb-1">Limited Time</p>
                    <h3 className="font-display text-2xl mb-1">{p.title}</h3>
                    <p className="text-sm text-white/75">{p.description}</p>
                    {p.valid_until && <p className="text-[10px] text-white/50 uppercase tracking-widest mt-2">Until {p.valid_until}</p>}
                  </div>
                  <div className="text-right">
                    {p.discount_percent > 0 && <p className="font-display text-5xl text-[var(--accent)]">{p.discount_percent}%</p>}
                    {p.discount_aed > 0 && <p className="font-display text-3xl text-[var(--accent)]">-{p.discount_aed} AED</p>}
                    <button onClick={() => setBookingOpen(true)} className="mt-2 text-xs uppercase tracking-widest text-[var(--accent)] hover:text-white" data-testid={`promo-claim-${p.id}`}>Book →</button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Privacy */}
        <section className="mb-10 bg-white border border-[var(--border)] p-6 flex items-center justify-between flex-wrap gap-4" data-testid="privacy-card">
          <div>
            <h3 className="font-display text-2xl">Profile Privacy</h3>
            <p className="text-sm text-[var(--muted)] mt-1">
              {user.share_profile ? "Your full profile is visible to staff." : "Only your name & phone are visible to general staff."}
            </p>
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only" checked={!!user.share_profile} onChange={togglePrivacy} data-testid="privacy-toggle" />
            <span className={`w-12 h-6 rounded-full relative transition-colors ${user.share_profile ? "bg-[var(--accent)]" : "bg-[var(--border)]"}`}>
              <span className={`absolute top-0.5 ${user.share_profile ? "right-0.5" : "left-0.5"} w-5 h-5 bg-white rounded-full shadow transition-all`} />
            </span>
            <span className="ml-3 text-xs uppercase tracking-widest text-[var(--muted)]">{user.share_profile ? "Sharing on" : "Sharing off"}</span>
          </label>
        </section>

        {/* Profile edit */}
        <ProfileEditCard user={user} refreshMe={refreshMe} />

        {/* Recs */}
        <section className="mb-12">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <p className="uppercase text-xs tracking-[0.3em] text-[var(--accent)] mb-1">Curated For You</p>
              <h2 className="font-display text-4xl">AI Recommendations</h2>
            </div>
            <button onClick={() => setBookingOpen(true)} className="btn-gold rounded-full px-6 py-2.5 text-xs tracking-widest uppercase" data-testid="account-book-btn">Book Now</button>
          </div>
          {recsLoading ? <p className="text-[var(--muted)]">Generating personalised picks…</p>
            : recs.length === 0 ? (
              <div className="bg-white border border-[var(--border)] p-7 text-[var(--muted)]" data-testid="recs-empty">
                Book your first treatment and our AI concierge will tailor recommendations just for you.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5" data-testid="recs-grid">
                {recs.map((r, i) => (
                  <article key={i} className="bg-white border border-[var(--border)] overflow-hidden">
                    <div className="aspect-[4/3] bg-[var(--bg)]"><img src={r.image_url || "https://images.unsplash.com/photo-1560750588-73207b1ef5b8?w=800&q=80&auto=format&fit=crop"} alt={r.name} onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1560750588-73207b1ef5b8?w=800&q=80&auto=format&fit=crop"; }} className="w-full h-full object-cover" /></div>
                    <div className="p-5">
                      <h3 className="font-display text-2xl mb-1">{r.name}</h3>
                      <p className="text-[var(--accent)] text-sm mb-2">AED {r.price_aed} · {r.duration_min} min</p>
                      <p className="text-sm text-[var(--muted)] italic leading-relaxed">"{r.reason}"</p>
                    </div>
                  </article>
                ))}
              </div>
            )}
        </section>

        {/* Bookings */}
        <section>
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <h2 className="font-display text-4xl">Your Bookings</h2>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setFeedbackOpen(true)} className="btn-outline rounded-full px-5 py-2 text-xs tracking-widest uppercase" data-testid="feedback-btn">
                <i className="fa-regular fa-comment mr-1.5"></i> Send Feedback
              </button>
              <button onClick={requestHistory} className="btn-outline rounded-full px-5 py-2 text-xs tracking-widest uppercase" data-testid="request-history-btn">
                <i className="fa-regular fa-envelope mr-1.5"></i> Request Records
              </button>
              <button onClick={downloadHistory} className="btn-gold rounded-full px-5 py-2 text-xs tracking-widest uppercase" data-testid="download-history-btn">
                <i className="fa-regular fa-file-pdf mr-1.5"></i> Download PDF
              </button>
            </div>
          </div>
          {reminders.length > 0 && (
            <div className="bg-gradient-to-r from-amber-50 to-rose-50 border border-[var(--accent)] p-4 mb-4 rounded-sm" data-testid="reminders-banner">
              <p className="text-xs uppercase tracking-widest text-[var(--accent)] mb-2 font-semibold">
                <i className="fa-regular fa-bell mr-1.5"></i> Upcoming Reminders
              </p>
              <ul className="space-y-1.5 text-sm">
                {reminders.map(r => (
                  <li key={r.id} data-testid={`reminder-${r.id}`}>
                    <span className="font-medium">{r.service}</span> on {r.date} at {r.time}
                    {" — "}
                    <span className={r.reminder_bucket === "2h" ? "text-rose-600 font-semibold" : "text-amber-700"}>
                      {r.reminder_bucket === "2h" ? `in ${r.hours_until}h — almost time!` : `in ${r.hours_until}h`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {bookings.length === 0 ? (
            <div className="bg-white border border-[var(--border)] p-7 text-[var(--muted)]" data-testid="bookings-empty">No bookings yet.</div>
          ) : (
            <div className="bg-white border border-[var(--border)] overflow-x-auto" data-testid="bookings-history">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bg)] text-xs uppercase tracking-widest text-[var(--muted)]">
                  <tr><th className="text-left p-4">Service</th><th className="text-left p-4">Date / Time</th><th className="text-left p-4">Status</th><th className="text-right p-4">Action</th></tr>
                </thead>
                <tbody>
                  {bookings.map(b => (
                    <tr key={b.id} className="border-t border-[var(--border)]" data-testid={`my-booking-${b.id}`}>
                      <td className="p-4 font-medium">{b.service}</td>
                      <td className="p-4">{b.date}<br/><span className="text-xs text-[var(--muted)]">{fmtTime12(b.time)}</span></td>
                      <td className="p-4"><StatusBadge s={b.status} /></td>
                      <td className="p-4 text-right">
                        {b.status === "cancelled" ? (
                          <span className="text-xs text-[var(--muted)] italic">{b.cancellation?.reason || "—"}</span>
                        ) : (
                          <button onClick={() => setCancelTarget({ id: b.id, reason: "" })} className="text-xs px-3 py-1.5 border border-red-300 text-red-600 hover:bg-red-50 rounded uppercase tracking-widest" data-testid={`cancel-${b.id}`}>Cancel</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} services={services} />
      <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      {cancelTarget && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="cancel-modal">
          <div className="bg-white max-w-md w-full border border-[var(--border)]">
            <div className="px-7 py-5 border-b border-[var(--border)] bg-[var(--bg)]">
              <h3 className="font-display text-2xl">Cancel Booking</h3>
            </div>
            <div className="p-7 space-y-4">
              <p className="text-sm text-[var(--muted)]">Please tell us why (optional). The reception will be notified.</p>
              <textarea value={cancelTarget.reason} onChange={e => setCancelTarget(c => ({...c, reason: e.target.value}))}
                rows={3} className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5 focus:outline-none focus:border-[var(--accent)]" data-testid="cancel-reason" />
              <div className="flex gap-3 justify-end">
                <button onClick={() => setCancelTarget(null)} className="btn-outline rounded-full px-5 py-2 text-xs tracking-widest uppercase">Keep It</button>
                <button onClick={cancelBooking} className="bg-red-600 hover:bg-red-700 text-white rounded-full px-5 py-2 text-xs tracking-widest uppercase" data-testid="confirm-cancel">Cancel Booking</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileEditCard({ user, refreshMe }) {  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user.name || "", email: user.email || "", phone: user.phone || "" });
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch("/customer/profile", form);
      await refreshMe();
      toast.success("Profile updated");
      setEditing(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed");
    } finally { setLoading(false); }
  };

  return (
    <section className="mb-10 bg-white border border-[var(--border)] p-6" data-testid="profile-card">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <h3 className="font-display text-2xl">My Profile</h3>
          <p className="text-sm text-[var(--muted)] mt-1">Update your name, email or mobile.</p>
        </div>
        {!editing && <button onClick={() => setEditing(true)} className="btn-outline rounded-full px-5 py-2 text-xs tracking-widest uppercase" data-testid="profile-edit-btn">Edit</button>}
      </div>
      {!editing ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div><p className="text-xs uppercase tracking-widest text-[var(--muted)]">Name</p><p>{user.name}</p></div>
          <div><p className="text-xs uppercase tracking-widest text-[var(--muted)]">Email</p><p>{user.email}</p></div>
          <div><p className="text-xs uppercase tracking-widest text-[var(--muted)]">Phone</p><p>{user.phone || "—"}</p></div>
        </div>
      ) : (
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input value={form.name} onChange={e=>upd("name", e.target.value)} placeholder="Name" data-testid="profile-name" className="bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <input type="email" value={form.email} onChange={e=>upd("email", e.target.value)} placeholder="Email" data-testid="profile-email" className="bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <input value={form.phone} onChange={e=>upd("phone", e.target.value)} placeholder="Phone" data-testid="profile-phone" className="bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <div className="md:col-span-3 flex gap-2 justify-end">
            <button type="button" onClick={() => setEditing(false)} className="btn-outline rounded-full px-5 py-2 text-xs tracking-widest uppercase">Cancel</button>
            <button type="submit" disabled={loading} className="btn-gold rounded-full px-5 py-2 text-xs tracking-widest uppercase" data-testid="profile-save">{loading ? "Saving..." : "Save"}</button>
          </div>
        </form>
      )}
    </section>
  );
}

function FeedbackModal({ open, onClose }) {
  const [form, setForm] = useState({ subject: "", message: "", contact_via: "any" });
  const [loading, setLoading] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (form.message.length < 5) return toast.error("Please share a bit more");
    setLoading(true);
    try {
      await api.post("/customer/feedback", form);
      toast.success("Thank you! Your feedback has been sent to the owner.");
      onClose();
      setForm({ subject: "", message: "", contact_via: "any" });
    } catch { toast.error("Failed to send"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="feedback-modal">
      <div className="bg-white max-w-md w-full border border-[var(--border)]">
        <div className="px-7 py-5 border-b border-[var(--border)] bg-[var(--bg)] flex justify-between items-center">
          <h3 className="font-display text-2xl">Feedback to Owner</h3>
          <button onClick={onClose} className="text-2xl">×</button>
        </div>
        <form onSubmit={submit} className="p-7 space-y-3">
          <input required placeholder="Subject" value={form.subject} onChange={e=>setForm({...form, subject: e.target.value})} data-testid="fb-subject" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <textarea required minLength={5} rows={5} placeholder="Your suggestion or feedback for Glazy..." value={form.message} onChange={e=>setForm({...form, message: e.target.value})} data-testid="fb-message" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <select value={form.contact_via} onChange={e=>setForm({...form, contact_via: e.target.value})} data-testid="fb-via" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5">
            <option value="any">Contact me anyway</option>
            <option value="email">Reply by email</option>
            <option value="whatsapp">Reply by WhatsApp</option>
          </select>
          <button type="submit" disabled={loading} className="btn-gold w-full rounded-full py-2.5 text-xs uppercase tracking-widest" data-testid="fb-submit">
            {loading ? "Sending..." : "Send to Owner"}
          </button>
        </form>
      </div>
    </div>
  );
}
